// Section theme definitions for Y2K arcade experience
export interface SectionTheme {
  name: string;
  primaryColor: string;
  secondaryColor: string;
  accentColor: string;
  glowColor: string;
  gradientStart: string;
  gradientEnd: string;
  beraMood: "patient" | "sweating" | "building" | "artistic" | "counting" | "excited";
  beraImage: string;
  decorativePattern?: string;
  cssVars: {
    "--theme-primary": string;
    "--theme-secondary": string;
    "--theme-accent": string;
    "--theme-glow": string;
    "--theme-gradient-start": string;
    "--theme-gradient-end": string;
    "--theme-text-shadow": string;
    "--theme-border-glow": string;
  };
}

export const sectionThemes: Record<string, SectionTheme> = {
  // Henlocker - Blue Security/Trust Theme
  lock: {
    name: "Henlocker",
    primaryColor: "#3E61FA",
    secondaryColor: "#4578FF",
    accentColor: "#F3C734",
    glowColor: "#3E61FA50",
    gradientStart: "#3E61FA",
    gradientEnd: "#4578FF",
    beraMood: "patient",
    beraImage: "/poku/lock.png",
    decorativePattern: "/assets/lock_texture.png",
    cssVars: {
      "--theme-primary": "#3E61FA",
      "--theme-secondary": "#4578FF",
      "--theme-accent": "#F3C734",
      "--theme-glow": "#3E61FA50",
      "--theme-gradient-start": "#3E61FA",
      "--theme-gradient-end": "#4578FF",
      "--theme-text-shadow": "2px 2px 0px #4578FF, -1px -1px 0px #3E61FA",
      "--theme-border-glow": "0 0 20px -5px #3E61FA30",
    },
  },

  // Incinerator - Red/Orange Fire Theme
  incinerator: {
    name: "Incinerator",
    primaryColor: "#D51A35",
    secondaryColor: "#FF6B35",
    accentColor: "#FFA500",
    glowColor: "#D51A3550",
    gradientStart: "#D51A35",
    gradientEnd: "#FF6B35",
    beraMood: "sweating",
    beraImage: "/poku/incinerator.png",
    decorativePattern: "/assets/fire_texture.png",
    cssVars: {
      "--theme-primary": "#D51A35",
      "--theme-secondary": "#FF6B35",
      "--theme-accent": "#FFA500",
      "--theme-glow": "#D51A3550",
      "--theme-gradient-start": "#D51A35",
      "--theme-gradient-end": "#FF6B35",
      "--theme-text-shadow": "2px 2px 0px #FF6B35, -1px -1px 0px #D51A35",
      "--theme-border-glow": "0 0 20px -5px #D51A3530",
    },
  },

  // Wall - Purple Strength Theme
  wall: {
    name: "Wall",
    primaryColor: "#9834c8",
    secondaryColor: "#7a2ba0",
    accentColor: "#c555f5",
    glowColor: "#9834c850",
    gradientStart: "#9834c8",
    gradientEnd: "#7a2ba0",
    beraMood: "building",
    beraImage: "/poku/gallery.png",
    decorativePattern: "/assets/brick_texture.png",
    cssVars: {
      "--theme-primary": "#9834c8",
      "--theme-secondary": "#7a2ba0",
      "--theme-accent": "#c555f5",
      "--theme-glow": "#9834c850",
      "--theme-gradient-start": "#9834c8",
      "--theme-gradient-end": "#7a2ba0",
      "--theme-text-shadow": "2px 2px 0px #7a2ba0, -1px -1px 0px #9834c8",
      "--theme-border-glow": "0 0 20px -5px #9834c830",
    },
  },

  // Creator/Art - Pink Creative Theme
  art: {
    name: "Creator",
    primaryColor: "#FF72B2",
    secondaryColor: "#C02EF0",
    accentColor: "#FF00FF",
    glowColor: "#FF72B250",
    gradientStart: "#FF72B2",
    gradientEnd: "#C02EF0",
    beraMood: "artistic",
    beraImage: "/poku/art.png",
    decorativePattern: "/assets/paint_texture.png",
    cssVars: {
      "--theme-primary": "#FF72B2",
      "--theme-secondary": "#C02EF0",
      "--theme-accent": "#FF00FF",
      "--theme-glow": "#FF72B250",
      "--theme-gradient-start": "#FF72B2",
      "--theme-gradient-end": "#C02EF0",
      "--theme-text-shadow": "2px 2px 0px #C02EF0, -1px -1px 0px #FF72B2",
      "--theme-border-glow": "0 0 20px -5px #FF72B230",
    },
  },

  // Vault - Green Success/Growth Theme
  vault: {
    name: "Vault",
    primaryColor: "#63DF77",
    secondaryColor: "#1CC75E",
    accentColor: "#FFD700",
    glowColor: "#63DF7750",
    gradientStart: "#63DF77",
    gradientEnd: "#1CC75E",
    beraMood: "counting",
    beraImage: "/poku/vault.png",
    decorativePattern: "/assets/coin_texture.png",
    cssVars: {
      "--theme-primary": "#63DF77",
      "--theme-secondary": "#1CC75E",
      "--theme-accent": "#FFD700",
      "--theme-glow": "#63DF7750",
      "--theme-gradient-start": "#63DF77",
      "--theme-gradient-end": "#1CC75E",
      "--theme-text-shadow": "2px 2px 0px #1CC75E, -1px -1px 0px #63DF77",
      "--theme-border-glow": "0 0 20px -5px #63DF7730",
    },
  },

  // Swap/Exchange - Blue/Cyan Tech Theme
  swap: {
    name: "Swap",
    primaryColor: "#00BFFF",
    secondaryColor: "#00CED1",
    accentColor: "#00FFFF",
    glowColor: "#00BFFF50",
    gradientStart: "#00BFFF",
    gradientEnd: "#00CED1",
    beraMood: "excited",
    beraImage: "/poku/exchange.png",
    decorativePattern: "/assets/circuit_texture.png",
    cssVars: {
      "--theme-primary": "#00BFFF",
      "--theme-secondary": "#00CED1",
      "--theme-accent": "#00FFFF",
      "--theme-glow": "#00BFFF50",
      "--theme-gradient-start": "#00BFFF",
      "--theme-gradient-end": "#00CED1",
      "--theme-text-shadow": "2px 2px 0px #00CED1, -1px -1px 0px #00BFFF",
      "--theme-border-glow": "0 0 20px -5px #00BFFF30",
    },
  },

  // Exchange - same as swap
  exchange: {
    name: "Swap",
    primaryColor: "#00BFFF",
    secondaryColor: "#00CED1",
    accentColor: "#00FFFF",
    glowColor: "#00BFFF50",
    gradientStart: "#00BFFF",
    gradientEnd: "#00CED1",
    beraMood: "excited",
    beraImage: "/poku/exchange.png",
    decorativePattern: "/assets/circuit_texture.png",
    cssVars: {
      "--theme-primary": "#00BFFF",
      "--theme-secondary": "#00CED1",
      "--theme-accent": "#00FFFF",
      "--theme-glow": "#00BFFF50",
      "--theme-gradient-start": "#00BFFF",
      "--theme-gradient-end": "#00CED1",
      "--theme-text-shadow": "2px 2px 0px #00CED1, -1px -1px 0px #00BFFF",
      "--theme-border-glow": "0 0 20px -5px #00BFFF30",
    },
  },

  // Mememaker - Green Creative Theme
  mememaker: {
    name: "Mememaker",
    primaryColor: "#66DD75",
    secondaryColor: "#4BC45B",
    accentColor: "#00FF00",
    glowColor: "#66DD7550",
    gradientStart: "#63DF77",
    gradientEnd: "#1CC75E",
    beraMood: "artistic",
    beraImage: "/poku/mememaker.png",
    decorativePattern: "/assets/meme_texture.png",
    cssVars: {
      "--theme-primary": "#66DD75",
      "--theme-secondary": "#4BC45B",
      "--theme-accent": "#00FF00",
      "--theme-glow": "#66DD7550",
      "--theme-gradient-start": "#63DF77",
      "--theme-gradient-end": "#1CC75E",
      "--theme-text-shadow": "2px 2px 0px #4BC45B, -1px -1px 0px #66DD75",
      "--theme-border-glow": "0 0 20px -5px #66DD7530",
    },
  },

  // Default theme (fallback)
  default: {
    name: "Henlo",
    primaryColor: "#F3C734",
    secondaryColor: "#3b82f6",
    accentColor: "#F3C734",
    glowColor: "#F3C73450",
    gradientStart: "#3a3a3f",
    gradientEnd: "#0a0a0b",
    beraMood: "excited",
    beraImage: "/assets/henlo_skate.png",
    cssVars: {
      "--theme-primary": "#F3C734",
      "--theme-secondary": "#3b82f6",
      "--theme-accent": "#F3C734",
      "--theme-glow": "#F3C73450",
      "--theme-gradient-start": "#3a3a3f",
      "--theme-gradient-end": "#0a0a0b",
      "--theme-text-shadow": "2px 2px 0px #3b82f6, -1px -1px 0px #1cc75e",
      "--theme-border-glow": "0 0 20px -5px #F3C73430",
    },
  },
};

// Helper function to get theme for a route
export function getThemeForRoute(pathname: string): SectionTheme {
  // Extract the segments and remove route groups (segments with parentheses)
  const segments = pathname.split("/").filter(Boolean);

  // Filter out route groups like (core) and get the actual route
  const routeSegments = segments.filter((segment) => !segment.startsWith("("));
  const firstSegment = routeSegments[0];

  // Return the matching theme or default
  return sectionThemes[firstSegment] || sectionThemes.default;
}

// Apply theme CSS variables to document
export function applyTheme(theme: SectionTheme): void {
  const root = document.documentElement;

  // Apply each CSS variable
  Object.entries(theme.cssVars).forEach(([key, value]) => {
    root.style.setProperty(key, value);
  });

  // Add theme class for specific overrides
  document.body.setAttribute("data-theme", theme.name.toLowerCase());
}
